import React, { useContext } from 'react';
import { buildClassName } from 'utils/build-class-name';
import styles from 'components/charts/address-page-charts.module.scss';
import openFeedback from 'utils/feedbackify';
import { IModalContext, IUserContext, ModalContext, UserContext } from 'contexts';
import AverageSold from '../../average-sold';
import ListVsSold from '../../list-vs-sold';
import DaysOnMarket from '../../days-on-market';
import SoldVolume from '../../sold-volume';
import { useTranslation } from 'react-i18next';
import { SiteNameSpace } from 'constants/locale';

import type Listing from 'data/listing';

interface Props {
  testId: string;
  listing: Listing;
  chartData?: Record<string, any>;
  neighbourhoodData?: Record<string, any>;
  isBlurred: boolean;
  type: 'market-stats' | 'market-competition';
  isOnModal?: boolean;
}

export default function ChartGroupLoading({ testId, listing, chartData, neighbourhoodData, isBlurred, type, isOnModal }: Props) {
  const { isAuthenticated } = useContext(UserContext) as IUserContext;
  const { openModal } = useContext(ModalContext) as IModalContext;
  const { t } = useTranslation(SiteNameSpace.Modal);
  const { providerId } = listing;

  const signIn = () => openModal('login-registration', { warningMessage: t('login-registration.form.signinRequiredWarning') });
  const openTermsModal = () => {
    if (isBlurred) {
      openModal('terms-of-use', { providerId });
    }
  };

  const renderFooter = () => {
    if (isBlurred && !isAuthenticated) {
      return (
        <div className={styles['login-cta']}>
          Real estate boards require that you sign in to see the full details of this property. <span onClick={signIn}> Log in or sign up </span> to see Avg. Sold Price and Sales Activity.
        </div>
      );
    } else if (isBlurred && isAuthenticated) {
      return (
        <div className={styles['login-cta']}>
          Real estate boards require that you accept Terms of use to see the full details of this property. <span onClick={openTermsModal}>Accept Terms of Use </span> to see Avg. Sold Price and Sales Activity.
        </div>
      );
    } else {
      return (
        <div className={styles.feedback}>
          Got feedback for our community market stats?
          <div className={styles.cta} onClick={openFeedback}>
            We’d love to hear it!
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <div className={styles['chart-wrapper']} data-testid={testId}>
        <div className={buildClassName(styles.charts, isOnModal && styles.modal)}>
          {type === 'market-stats' &&
            <>
              <AverageSold
                listing={listing}
                chartData={chartData}
                neighbourhoodData={neighbourhoodData}
                isBlurred={isBlurred}
              />
              <ListVsSold
                listing={listing}
                chartData={chartData}
                isBlurred={isBlurred}
              />
            </>
          }
          {type === 'market-competition' &&
            <>
              <DaysOnMarket
                listing={listing}
                chartData={chartData}
                neighbourhoodData={neighbourhoodData}
                isBlurred={isBlurred}
              />
              <SoldVolume
                listing={listing}
                chartData={chartData}
                neighbourhoodData={neighbourhoodData}
                isBlurred={isBlurred}
              />
            </>
          }
        </div>
        {renderFooter()}
      </div>
    </>
  );
}
